import React, { useEffect, useState, useMemo, ReactElement } from 'react';
import TAutocomplete from './types/TAutocomplete';
import debounce from 'lodash.debounce';

import Dropdown from '../../structural-components/Header/components/Dropdown/Dropdown';
import { Link, MaskedLink } from '../Link';
import { MultilineAttribute } from '../Attribute';
import DropdownTitleWithLink from './DropdownTitleWithLink';
import Icon from '../Icon/Icon';

const DEBOUNCE_DELAY = 200;
const MAX_SAVED_SEARCHES = 2;

const getDropdownOptions = (
  predefinedSuggestions,
  suggestionRedirectDisabled,
  isNewHeader = false,
  typedValue = '',
  onFillSuggestion?: (suggestion: string) => () => void,
) =>
  predefinedSuggestions.map(({ query, searchLink, otherCriteria, type = '', ...rest }) => {
    const handleLinkClick = suggestionRedirectDisabled ? (e) => e.preventDefault() : undefined;
    const link = suggestionRedirectDisabled ? '#' : searchLink;

    return {
      ...rest,
      otherCriteria,
      searchLink,
      modifiers: [type],
      value: query,
      label: (
        <Link onClick={handleLinkClick} href={link} kind="block">
          <MultilineAttribute
            icon={isNewHeader ? 'expired' : 'search'}
            title={query}
            description={otherCriteria}
            typedValue={typedValue}
            onSuggestionFill={onFillSuggestion?.(query)}
          />
        </Link>
      ),
    };
  });
const getDropdownSavedSearchesOptions = (
  saveSearchesSuggestions,
  typedValue = '',
  onFillSuggestion?: (suggestion: string) => () => void,
) =>
  saveSearchesSuggestions.map(({ title, url, searchType, attributes, query, ...rest }) => {
    return {
      ...rest,
      otherCriteria: attributes,
      searchLink: url,
      clickable: true,
      modifiers: [searchType],
      value: query,
      label: (
        <MaskedLink kind="block" href={url}>
          <MultilineAttribute
            icon="heart"
            title={title}
            description={attributes}
            typedValue={typedValue}
            onSuggestionFill={onFillSuggestion?.(query)}
          />
        </MaskedLink>
      ),
    };
  });

// todo: Simplify if possible
const getFilteredSuggestions = (list, value) => {
  if (!value) {
    return list;
  }

  const escapedValue = value.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
  const regex = new RegExp(`^${escapedValue}`, 'i');

  return list.filter((suggestion) => suggestion.query?.match(regex) !== null);
};

const getFilteredAndLimitedSuggestions = (list, value, limit) => {
  return getFilteredSuggestions(list, value).slice(0, limit);
};

const EMPTY_ARRAY = [];

const Autocomplete = ({
  value,
  xsrfToken,
  suggestionsQueryUrl,
  clearPredefinedSuggestionsUrl,
  predefinedSuggestions = EMPTY_ARRAY,
  savedSearches = EMPTY_ARRAY,
  suggestionCategory = '',
  onSelect,
  onChange,
  name,
  autoComplete = 'off',
  suggestionRedirectDisabled = false,
  isNewHeader = false,
  withSearchField = false,
  highlightUntypedSuggestions = false,
  showSuggestionFillButton = false,
  isWiderSearchBar = false,
  onQueryBuilderClick,
  ariaLabel,
  translations,
  searchBarTranslationsForTenant,
  ...props
}: TAutocomplete) => {
  const [currentValue, setCurrentValue] = useState<any>(value || '');
  const [recentSuggestions, setRecentSuggestions] = useState(predefinedSuggestions);
  const [saveSearchesSuggestions, setSavedSearchesSuggestions] = useState(savedSearches);
  const [newSuggestions, setNewSuggestions] = useState([]);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    setRecentSuggestions(predefinedSuggestions);
  }, [predefinedSuggestions]);

  useEffect(() => {
    setSavedSearchesSuggestions(savedSearches);
  }, [savedSearches]);
  /**
   * Removes recent suggestions from the list
   */
  const handleClearRecentSuggestions = () => {
    setCurrentValue(value);

    const headers = {
      ...(xsrfToken && {
        'x-mp-xsrf': xsrfToken,
      }),
    };

    if (clearPredefinedSuggestionsUrl) {
      fetch(clearPredefinedSuggestionsUrl, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers,
      }).catch(() => {});
    }

    setRecentSuggestions([...recentSuggestions.slice(predefinedSuggestions.length)]);
  };
  const handleClickSavedSearches = () => {
    window.location.href = '/my-account/saved-searches/index.html';
  };
  const fetchSuggestions = useMemo(
    () =>
      debounce((prefix) => {
        const url = `${suggestionsQueryUrl}?prefix=${encodeURIComponent(
          prefix.toLowerCase(),
        )}&category=${encodeURIComponent(suggestionCategory)}`;

        fetch(url, {
          method: 'GET',
          cache: 'default',
        })
          .then((response) => response.json())
          .then((json) => {
            const newFetchedSuggestions = json.completions.map(({ completion }) => ({
              category: json.category,
              query: completion,
              type: 'api',
            }));

            setNewSuggestions(newFetchedSuggestions);
          })
          .catch(() => {});
      }, DEBOUNCE_DELAY),
    [suggestionsQueryUrl, suggestionCategory],
  );

  useEffect(() => {
    return () => {
      fetchSuggestions.cancel();
    };
  }, [fetchSuggestions]);

  const handleChange = (event) => {
    const newValue = event.target.value;
    setCurrentValue(newValue);

    if (onChange) {
      onChange(newValue);
    }

    if (newValue !== value) {
      if (newValue !== '' && suggestionsQueryUrl) {
        fetchSuggestions(newValue);
      } else {
        setRecentSuggestions(predefinedSuggestions);
        setSavedSearchesSuggestions(savedSearches);
        setNewSuggestions([]);
      }
    }
  };

  const handleItemSelect = (item, isSubmit = false) => {
    if (typeof item !== 'undefined') {
      setCurrentValue(item.value);

      if (onSelect) {
        onSelect(item, isSubmit);
      }
    }
  };

  const handleItemNavigation = (item) => {
    const newValue = item ? item.value : value;
    setCurrentValue(newValue);
  };

  const handleSuggestionFill = (suggestion: string) => () => {
    setCurrentValue(suggestion);
    onChange?.(suggestion);
    onQueryBuilderClick?.();
    if (suggestion !== value) {
      if (suggestion !== '' && suggestionsQueryUrl) {
        fetchSuggestions(suggestion);
      } else {
        setRecentSuggestions(predefinedSuggestions);
        setSavedSearchesSuggestions(savedSearches);
        setNewSuggestions([]);
      }
    }
  };

  const filteredSuggestions = getFilteredSuggestions(recentSuggestions, value);

  const filteredSavedSearches = getFilteredAndLimitedSuggestions(
    saveSearchesSuggestions,
    currentValue,
    MAX_SAVED_SEARCHES,
  );

  const MAX_CLICKABLE_OPTIONS = 10;

  const buildLimitedOptions = (saved, recent, apiSuggestions) => {
    const limitedOptions: Array<{
      modifiers?: string[];
      clickable: boolean;
      label: string | ReactElement;
      value: string | number;
      onClick?: () => void;
    }> = [];

    let clickableCount = 0;

    const addSection = (
      title: string,
      linkTitle: string | undefined,
      url: string,
      items: any[],
      onClick: (() => void) | null = null, // Define the type for onClick to allow null or function
    ) => {
      if (items.length > 0 && clickableCount < MAX_CLICKABLE_OPTIONS) {
        limitedOptions.push({
          modifiers: ['horizontal'],
          clickable: false,
          value: title,
          label: (
            <DropdownTitleWithLink
              title={title}
              linkTitle={linkTitle ?? linkTitle}
              url={url}
              onClick={onClick ?? undefined}
            />
          ),
        });

        for (const item of items) {
          if (clickableCount < MAX_CLICKABLE_OPTIONS) {
            limitedOptions.push(item);
            clickableCount++;
          } else {
            break;
          }
        }
      }
    };

    addSection(
      searchBarTranslationsForTenant?.savedSearchesTitle,
      searchBarTranslationsForTenant?.savedSearchesLinkTitle,
      '/my-account/saved-searches/index.html',
      getDropdownSavedSearchesOptions(
        saved,
        highlightUntypedSuggestions ? currentValue : undefined,
        !isWiderSearchBar && showSuggestionFillButton ? handleSuggestionFill : undefined,
      ),
      handleClickSavedSearches,
    );

    addSection(
      searchBarTranslationsForTenant?.recentSearchesTitle,
      searchBarTranslationsForTenant?.recentSearchesLinkTitle,
      '#',
      getDropdownOptions(
        recent,
        suggestionRedirectDisabled,
        isNewHeader,
        highlightUntypedSuggestions ? currentValue : undefined,
        !isWiderSearchBar && showSuggestionFillButton ? handleSuggestionFill : undefined,
      ),
      handleClearRecentSuggestions,
    );

    addSection(
      searchBarTranslationsForTenant?.suggestionsTitle,
      undefined,
      '',
      getDropdownOptions(
        apiSuggestions,
        false,
        isNewHeader,
        highlightUntypedSuggestions ? currentValue : undefined,
        showSuggestionFillButton ? handleSuggestionFill : undefined,
      ),
    );

    return limitedOptions;
  };

  const limitedOptions = buildLimitedOptions(filteredSavedSearches, filteredSuggestions, newSuggestions);

  const dropdown = (
    <Dropdown
      {...props}
      containerModifiers={['full-width']}
      autoComplete={autoComplete}
      htmlNameAttr={name}
      as="input"
      toggleClassName={
        withSearchField ? 'hz-Header-Autocomplete-input hz-SearchTextField-input' : 'hz-Header-Autocomplete-input'
      }
      value={currentValue}
      options={limitedOptions}
      toggleOnClick={false}
      onChange={handleChange}
      onItemNavigation={handleItemNavigation}
      onItemSelect={handleItemSelect}
      translations={translations}
      ariaLabel={translations?.dropdownAriaLabel ?? ariaLabel}
    />
  );

  if (!withSearchField) {
    return dropdown;
  }

  const handleClear = () => {
    setCurrentValue('');
    setRecentSuggestions(predefinedSuggestions);
    setSavedSearchesSuggestions(savedSearches);
    setNewSuggestions([]);
    if (onChange) onChange('');
  };

  return (
    <div className="hz-SearchTextField">
      <Icon name="search" className="hz-SearchTextField-icon hz-SearchTextField-searchIcon" />
      {dropdown}
      {currentValue && (
        <button
          type="button"
          className="hz-SearchTextField-clearButton"
          onClick={handleClear}
          aria-label="Clear search"
        >
          <Icon name="close" className="hz-SearchTextField-icon hz-SearchTextField-clearIcon" />
        </button>
      )}
    </div>
  );
};

export default Autocomplete;
